import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { Alert, Button, Field, Select, TextInput } from '../components/ui';
import { useMutation, compact, numOrUndef } from '../lib/useMutation';
import '../styles/landing.css';
import '../styles/public.css';

/**
 * Request a demo. Collects the detail a sales conversation needs, validates it
 * on the client for immediate feedback, then posts to the public
 * /demo-requests endpoint, which stores the request and notifies the team. On
 * success the form is replaced by a confirmation, so the visitor is never left
 * wondering whether it went through.
 */

interface Errors { [k: string]: string }

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function RequestDemo() {
  const { run, busy, error } = useMutation();
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [form, setForm] = useState({
    fullName: '', company: '', email: '', phone: '',
    propertyCount: '', unitCount: '', preferredDate: '', preferredTime: '', message: '',
  });

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  function validate(): Errors {
    const err: Errors = {};
    if (!form.fullName.trim()) err.fullName = 'Please tell us your name.';
    if (!form.email.trim()) err.email = 'An email is needed so we can reply.';
    else if (!EMAIL_RE.test(form.email.trim())) err.email = 'That email does not look right.';
    if (!form.phone.trim()) err.phone = 'A phone number helps us reach you.';
    return err;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length) return;

    const body = compact({
      fullName: form.fullName.trim(),
      company: form.company.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      propertyCount: numOrUndef(form.propertyCount),
      unitCount: numOrUndef(form.unitCount),
      preferredDate: form.preferredDate || undefined,
      preferredTime: form.preferredTime || undefined,
      message: form.message.trim(),
    });

    try {
      await run('/api/v1/demo-requests', { body });
      setDone(true);
      window.scrollTo(0, 0);
    } catch { /* useMutation surfaces the error below */ }
  }

  return (
    <div className="lp mk">
      <PublicHeader />

      {done ? (
        <div className="mk-success">
          <div className="mk-success-mark" aria-hidden>✓</div>
          <h1 className="mk-h2">Thank you, your request is in</h1>
          <p className="mk-p">
            We have received your request and sent a confirmation to{' '}
            <strong>{form.email.trim()}</strong>. Our team will be in touch shortly to
            arrange your walkthrough.
          </p>
          <div className="mk-cta-row" style={{ marginTop: 'var(--space-6)' }}>
            <Link className="btn btn-primary" to="/">Back to home</Link>
            <Link className="btn btn-secondary" to="/register">Get started instead</Link>
          </div>
        </div>
      ) : (
        <>
          <section className="mk-hero">
            <span className="mk-eyebrow">Request a demo</span>
            <h1 className="mk-title">See your property business think ahead</h1>
            <p className="mk-lede">
              Tell us a little about your properties and we will walk you through the platform
              with your portfolio in mind: how rent, tenants and maintenance come together,
              and how NyumbaSuite surfaces what needs your attention first.
            </p>
          </section>

          <div className="mk-form-wrap">
            <form className="card" onSubmit={onSubmit} noValidate>
              {error && (
                <div style={{ marginBottom: 'var(--space-4)' }}>
                  <Alert tone="danger">{error.message}</Alert>
                </div>
              )}
              <div className="mk-form-grid">
                <Field label="Full name" htmlFor="fullName" error={errors.fullName}>
                  <TextInput id="fullName" value={form.fullName} onChange={set('fullName')}
                    invalid={!!errors.fullName} autoComplete="name" />
                </Field>
                <Field label="Company (optional)" htmlFor="company">
                  <TextInput id="company" value={form.company} onChange={set('company')}
                    autoComplete="organization" />
                </Field>
                <Field label="Email" htmlFor="email" error={errors.email}>
                  <TextInput id="email" type="email" value={form.email} onChange={set('email')}
                    invalid={!!errors.email} autoComplete="email" />
                </Field>
                <Field label="Phone" htmlFor="phone" error={errors.phone}>
                  <TextInput id="phone" type="tel" value={form.phone} onChange={set('phone')}
                    invalid={!!errors.phone} autoComplete="tel" placeholder="+254 7XX XXX XXX" />
                </Field>
                <Field label="Number of properties" htmlFor="propertyCount">
                  <TextInput id="propertyCount" type="number" min={0} value={form.propertyCount}
                    onChange={set('propertyCount')} />
                </Field>
                <Field label="Number of units" htmlFor="unitCount">
                  <TextInput id="unitCount" type="number" min={0} value={form.unitCount}
                    onChange={set('unitCount')} />
                </Field>
                <Field label="Preferred date (optional)" htmlFor="preferredDate">
                  <TextInput id="preferredDate" type="date" value={form.preferredDate}
                    onChange={set('preferredDate')} />
                </Field>
                <Field label="Preferred time (optional)" htmlFor="preferredTime">
                  <Select id="preferredTime" value={form.preferredTime} onChange={set('preferredTime')}>
                    <option value="">No preference</option>
                    <option value="morning">Morning (8am to 12pm)</option>
                    <option value="afternoon">Afternoon (12pm to 4pm)</option>
                    <option value="evening">Evening (4pm to 6pm)</option>
                  </Select>
                </Field>
                <div className="full">
                  <Field label="Anything you would like us to know? (optional)" htmlFor="message">
                    <textarea id="message" className="input" rows={4} value={form.message}
                      onChange={set('message')} style={{ minHeight: 96, padding: 'var(--space-3)', resize: 'vertical' }} />
                  </Field>
                </div>
              </div>
              <Button type="submit" block loading={busy}>Request my demo</Button>
              <p className="field-hint" style={{ marginTop: 'var(--space-3)', textAlign: 'center' }}>
                Prefer to explore on your own? <Link to="/register">Create your account</Link>, no demo needed.
              </p>
            </form>
          </div>
        </>
      )}

      <SiteFooter />
    </div>
  );
}
